import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  GlobeIcon,
  ShieldCheckIcon,
  TelegramLogoIcon,
  ThermometerIcon,
  WarningIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PhoneInput,
  StatusChip,
  isCompleteRuPhone,
  type Tone,
} from '@smena/ui';
import { IgnoredChatsDialog } from '../channel-settings/IgnoredChatsDialog';
import { WarmupBlock } from '../channel-settings/WarmupBlock';
import {
  addTgIgnored,
  connectTgSession,
  disconnectTgSession,
  fetchTgDialogs,
  fetchTgIgnored,
  fetchTgSession,
  fetchTgSessions,
  removeTgIgnored,
  setTgProxy,
  submitTgPassword,
  type TgPersonalSession,
  type TgPersonalStatus,
  type TgWarmup,
} from './api';

const STATUS_LABEL: Record<TgPersonalStatus, string> = {
  qr_pending: 'ждём скан QR',
  password_required: 'нужен облачный пароль',
  authorized: 'подключён',
  degraded: 'связь нестабильна',
  flood_wait: 'пауза Telegram',
  disconnected: 'отключён',
  banned: 'аккаунт ограничен',
};

const STATUS_TONE: Record<TgPersonalStatus, Tone> = {
  qr_pending: 'amber',
  password_required: 'amber',
  authorized: 'green',
  degraded: 'amber',
  flood_wait: 'amber',
  disconnected: 'red',
  banned: 'red',
};

// Темп поллинга у каждого статуса свой.
// В qr_pending шлюз ротирует ссылку примерно раз в 30 секунд, и картинку надо перерисовывать -
// отсюда 2 секунды. Живая сессия меняется редко, ей хватает 15. Терминальные статусы сами не
// оживут: там поллинг только жёг бы запросы.
const POLL_MS: Record<TgPersonalStatus, number | false> = {
  qr_pending: 2_000,
  password_required: 2_000,
  authorized: 15_000,
  degraded: 15_000,
  flood_wait: 15_000,
  disconnected: false,
  banned: false,
};

/**
 * «Telegram - личный аккаунт УК»: гость пишет на обычный номер, отвечает помощник с того же номера.
 *
 * Отдельная карточка, а не строка в списке каналов: у привязки свой жизненный цикл (QR, 2FA,
 * прогрев, прокси, разрыв) и своя цена ошибки - на кону личный аккаунт клиента, а не наш бот.
 */
export function TgPersonalCard() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [phone, setPhone] = useState('');

  const listQ = useQuery({
    queryKey: ['tg-personal'],
    queryFn: fetchTgSessions,
    refetchInterval: 15_000,
  });

  const connectMut = useMutation({
    mutationFn: () =>
      connectTgSession({
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      }),
    onSuccess: () => {
      setConnecting(false);
      setPhone('');
      void qc.invalidateQueries({ queryKey: ['tg-personal'] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Не удалось начать подключение'),
  });

  const disconnectMut = useMutation({
    mutationFn: disconnectTgSession,
    onSuccess: () => {
      toast.success('Аккаунт отвязан');
      void qc.invalidateQueries({ queryKey: ['tg-personal'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отвязать'),
  });

  const sessions = listQ.data?.items ?? [];
  const available = listQ.data?.available ?? false;
  // Пока одна привязка не доведена до конца, вторую не начинаем: лишние QR-логины сами по себе
  // повод для Telegram присмотреться к номеру.
  const pending = sessions.some(
    (s) => s.status === 'qr_pending' || s.status === 'password_required',
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TelegramLogoIcon weight="bold" className="size-4 text-accent-fg" />
          Telegram - личный аккаунт УК
        </CardTitle>
        <p className="text-xs text-muted">
          Гость пишет на ваш обычный номер, как живому человеку, а отвечает цифровой помощник с
          того же номера. Аккаунт привязывается как ещё одно устройство: Настройки → Устройства →
          Подключить устройство.
        </p>
      </CardHeader>

      <CardContent className="flex flex-col gap-3 text-sm">
        {listQ.data && !available ? (
          <div className="rounded-md border border-line bg-panel2/40 px-3 py-2 text-xs text-muted">
            Шлюз не настроен на этом стенде (<code>TG_GATEWAY_URL</code>). Подключить аккаунт пока
            нельзя - напишите в поддержку Smena.
          </div>
        ) : null}

        {available && sessions.length === 0 && !connecting ? (
          <p className="text-muted">Аккаунт не подключён.</p>
        ) : null}

        {sessions.map((s) => (
          <SessionRow
            key={s.sessionId}
            session={s}
            removing={disconnectMut.isPending}
            onRemove={() => disconnectMut.mutate(s.sessionId)}
          />
        ))}

        {connecting && !pending ? (
          <form
            className="flex flex-col gap-3 rounded-md border border-line bg-panel2/40 p-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              connectMut.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tgp-phone">Номер аккаунта (необязательно, для наглядности)</Label>
              <PhoneInput id="tgp-phone" value={phone} onChange={setPhone} />
              {phone && !isCompleteRuPhone(phone) ? (
                <span className="text-[11px] text-red">
                  Номер неполный: нужно 11 цифр, начиная с 7. Пока поддерживаем только российские номера.
                </span>
              ) : null}
            </div>


            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={connectMut.isPending || (phone.length > 0 && !isCompleteRuPhone(phone))}
              >
                {connectMut.isPending ? 'Получаем QR…' : 'Получить QR'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setConnecting(false)}
              >
                Отмена
              </Button>
            </div>
          </form>
        ) : null}

        {available && !connecting && !pending ? (
          <Button size="sm" className="self-start" onClick={() => setConnecting(true)}>
            Подключить аккаунт
          </Button>
        ) : null}

        {/* Информированный opt-in: риск несёт личный аккаунт клиента, и он должен увидеть его
            до привязки, а не после ограничения. Рядом - чем мы этот риск снижаем. */}
        <div className="flex gap-2 rounded-md border border-amber/30 bg-amber/10 px-3 py-2 text-xs text-amber">
          <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
          <div className="space-y-1">
            <p>
              Привязка идёт через неофициальный клиент Telegram. Telegram это видит и держит такие
              аккаунты под наблюдением - номер могут ограничить.
            </p>
            <p>
              Если это произойдёт, канал сам переключит гостей на общий бот Smena: без ответа никто
              не останется.
            </p>
          </div>
        </div>

        <div className="flex gap-2 rounded-md border border-line bg-panel2/40 px-3 py-2 text-xs text-muted">
          <ShieldCheckIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Что мы делаем, чтобы до этого не дошло: держим темп живой переписки вместо рассылки,
            первые сутки после привязки почти не пишем, даём каждому номеру отдельный прокси и не
            отправляем один и тот же текст разным гостям.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function SessionRow({
  session,
  onRemove,
  removing,
}: {
  session: TgPersonalSession;
  onRemove: () => void;
  removing: boolean;
}) {
  // Отдельный запрос на сессию: список обновляется раз в 15 секунд, а QR столько не живёт.
  const liveQ = useQuery({
    queryKey: ['tg-personal', session.sessionId],
    queryFn: () => fetchTgSession(session.sessionId),
    initialData: session,
    refetchInterval: (q) => POLL_MS[q.state.data?.status ?? session.status],
  });
  const s = liveQ.data ?? session;

  // Сессия «живая»: вход состоялся, аккаунт ещё не потерян. Прокси и прогрев показываем только тут.
  const live = s.status === 'authorized' || s.status === 'degraded' || s.status === 'flood_wait';
  // Привязка ещё идёт: кнопка справа тогда не «Отвязать», а «Отменить» - терять нечего.
  const binding = s.status === 'qr_pending' || s.status === 'password_required';
  const hint = statusHint(s);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate">
            {s.phone ? `+${s.phone}` : 'номер появится после входа'}
          </div>
          <div className="font-mono text-[11px] text-muted">{s.sessionId.slice(0, 8)}…</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusChip tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</StatusChip>
          {/* Настройки - только у живой сессии: у незавершённой привязки настраивать нечего. */}
          {live ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSettingsOpen(true)}
              aria-label="Настройки аккаунта"
            >
              <GearSixIcon className="size-3.5" />
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={removing}
            onClick={() => {
              if (
                binding ||
                confirm('Отвязать аккаунт? Гости перестанут получать ответы на этот номер в Telegram.')
              ) {
                onRemove();
              }
            }}
          >
            {binding ? 'Отменить' : 'Отвязать'}
          </Button>
        </div>
      </div>

      {hint ? (
        <div
          className={
            s.status === 'banned' || s.status === 'disconnected'
              ? 'rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive'
              : 'rounded-md border border-amber/30 bg-amber/10 px-3 py-2 text-xs text-amber'
          }
        >
          {hint}
        </div>
      ) : null}

      {s.lastError ? <div className="text-[11px] text-destructive">{s.lastError}</div> : null}

      {s.status === 'qr_pending' && s.qr ? <QrBlock qr={s.qr} /> : null}
      {s.status === 'password_required' ? <PasswordForm sessionId={s.sessionId} /> : null}

      {live ? (
        <>
          <WarmupBlock state={s.warmupState} />
        </>
      ) : null}

      <IgnoredChatsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        subtitle={s.phone ? `Telegram, +${s.phone}` : 'Telegram - личный аккаунт'}
        queryKey={['tg-personal', s.sessionId, 'ignored']}
        fetchRules={() => fetchTgIgnored(s.sessionId)}
        addRule={(chatId, note) => addTgIgnored(s.sessionId, chatId, note)}
        removeRule={(ruleId) => removeTgIgnored(s.sessionId, ruleId)}
        fetchDialogs={async () => {
          const res = await fetchTgDialogs(s.sessionId, 100);
          return {
            items: res.items.map((d) => ({
              chatId: d.peerId,
              title: d.title,
              ...(d.phone ? { phone: d.phone } : {}),
              ...(d.username ? { username: d.username } : {}),
            })),
          };
        }}
      />
    </div>
  );
}

function QrBlock({ qr }: { qr: NonNullable<TgPersonalSession['qr']> }) {
  const stale = qr.expiresAtMs > 0 && Date.now() > qr.expiresAtMs;
  return (
    <div className="flex flex-col items-center gap-2 py-2">
      {/* Белая подложка обязательна: сканеры не читают QR на цветном фоне. */}
      <img src={qr.image} alt="QR для входа в Telegram" className="size-56 rounded-md bg-white p-2" />
      <ol className="list-inside list-decimal text-xs text-muted">
        <li>На телефоне: Telegram → Настройки → Устройства</li>
        <li>«Подключить устройство» и наведите камеру на этот код</li>
      </ol>
      <p className="text-center text-xs text-muted">
        {stale
          ? 'Обновляем код…'
          : 'Код обновляется сам каждые 30 секунд - держите страницу открытой.'}
      </p>
      <p className="text-center text-[11px] text-muted">
        Если на аккаунте включён облачный пароль, после скана попросим его здесь.
      </p>
    </div>
  );
}

function PasswordForm({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const mut = useMutation({
    mutationFn: () => submitTgPassword(sessionId, password),
    onSuccess: () => {
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['tg-personal'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Пароль не принят'),
  });

  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mut.mutate();
      }}
    >
      <Label htmlFor={`tgp-pw-${sessionId}`}>Облачный пароль Telegram (двухэтапная проверка)</Label>
      <div className="flex gap-2">
        <Input
          id={`tgp-pw-${sessionId}`}
          type="password"
          autoComplete="one-time-code"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" size="sm" disabled={mut.isPending || !password}>
          {mut.isPending ? 'Проверяем…' : 'Войти'}
        </Button>
      </div>
      <p className="text-xs text-muted">Пароль нужен только для входа и у нас не сохраняется.</p>
    </form>
  );
}

/** Прогрев - главная непонятная для УК часть: объясняем, почему помощник «молчит». */


/** Что делать оператору при каждом нештатном статусе. Молчаливый красный чип бесполезен. */
function statusHint(s: TgPersonalSession): string | null {
  switch (s.status) {
    case 'degraded': {
      return (
        'Шлюз не может достучаться до Telegram. Чаще всего дело в прокси платформы - напишите в поддержку Smena, ' +
        'если адрес перестал отвечать. Сообщения гостей не теряются: Telegram отдаст их, когда связь вернётся.'
      );
    }
    case 'flood_wait': {
      const left = s.floodUntil ? fmtLeft(s.floodUntil) : null;
      return (
        `Telegram притормозил отправку с этого номера${left ? ` примерно на ${left}` : ''}. ` +
        'Ответы встанут в очередь и уйдут сами, делать ничего не нужно.'
      );
    }
    case 'banned': {
      return (
        'Telegram ограничил аккаунт. Из панели это не чинится - обращаться нужно в поддержку Telegram ' +
        'с этого номера. Пока гостям отвечает общий бот Smena.'
      );
    }
    case 'disconnected': {
      return (
        'Сессия завершена - обычно так бывает, когда устройство отключили в Telegram вручную. ' +
        'Подключите аккаунт заново, для этого понадобится телефон.'
      );
    }
    default:
      return null;
  }
}

/** Сколько осталось до момента в будущем: «4 мин», «2 ч». */
function fmtLeft(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const min = Math.ceil(ms / 60_000);
  return min < 60 ? `${min} мин` : `${Math.ceil(min / 60)} ч`;
}
